import { Skeleton } from 'antd';
import React, { useCallback } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { useGetEventsQuery } from '../searchSlice';
import AdultsOnly from '../assets/images/adults-only.svg';
import Wheelchair from '../assets/images/wheelchair.svg';
import boy from '../assets/images/boy.svg';
import Free from '../assets/images/free.svg';
import Saltwater from '../assets/images/saltwater.svg';
import Freshwater from '../assets/images/freshwater.svg';
import Carp from '../assets/images/Carp.svg';
import Flyfishing from '../assets/images/fly.svg';
import Licence from '../assets/images/licensing.svg';
import Castaward from '../assets/images/castAwards.svg';

function SearchFilterCard({ imageSrc, name, active = false, onClick }) {
  return (
    <div
      className={
        'slider-content search-filter-card cp ' +
        (active ? 'search-filter-card--active' : 'search-filter-card--inactive')
      }
      onClick={onClick}
    >
      <div className='search-filter-card__image brFull mg-bottom-8'>
        <img src={imageSrc} alt={name} />
      </div>
      <p className='sb-blue-600 fs-12 gSemiBold w-full txt-center mg-bottom-0'>
        {name.length > 12 ? name.slice(0, 12) + '...' : name}
      </p>
    </div>
  );
}

function SkeletonSearchFilterCard() {
  return (
    <div className='slider-content search-filter-card cp '>
      <div className='search-filter-card__image mg-bottom-8'>
        <Skeleton.Avatar
          active
          className={'w-full h-full'}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      <p className='fs-12 w-full txt-center mg-bottom-0'>
        <Skeleton active size={'small'} paragraph={false} />
      </p>
    </div>
  );
}

function MapQuickFilters({ selected = [], onSelectionChange }) {
  const { data: events, isFetching: loadingFilters } = useGetEventsQuery();

  const FILTERS = [
    {
      name: 'Cast Awards',
      label: 'is_cast_award',
      icon: Castaward,
    },
    {
      name: 'Wheelchair Access',
      label: 'is_wheelchair_accessible',
      icon: Wheelchair,
    },
    {
      name: 'Children Allowed',
      label: 'is_children_allowed',
      icon: boy,
    },
    {
      name: 'Adults Only',
      label: 'is_adults_only',
      icon: AdultsOnly,
    },
    {
      name: 'Free',
      label: 'is_free',
      icon: Free,
    },
    {
      name: 'Rod Licence Waiver',
      label: 'has_rod_licence_waiver',
      icon: Licence,
    },
  ];

  const DISCIPLINE_FILTERS = [
    {
      name: 'Fly Fishing',
      label: 'fly_fishing',
      icon: Flyfishing,
    },
    {
      name: 'Freshwater',
      label: 'freshwater',
      icon: Freshwater,
    },
    {
      name: 'Saltwater',
      label: 'saltwater',
      icon: Saltwater,
    },
    {
      name: 'Carp',
      label: 'carp',
      icon: 'https://s3-eu-west-2.amazonaws.com/swimbooker-fishery-details/sb_search_tags/carp.svg',
    },
    {
      name: 'Predator',
      label: 'predator',
      icon: 'https://s3-eu-west-2.amazonaws.com/swimbooker-fishery-details/sb_search_tags/predator.svg',
    },
  ];

  const handleFilterClick = useCallback(
    (key) => {
      const wasAlreadySelected = selected?.event_type_ids?.includes(key);
      onSelectionChange(
        'event_type_ids',
        wasAlreadySelected
          ? selected?.event_type_ids?.filter((s) => s !== key)
          : [...selected?.event_type_ids, key]
      );
    },
    [onSelectionChange, selected]
  );

  const handleDisciplineFilterClick = useCallback(
    (key) => {
      const wasAlreadySelected = selected?.disciplines?.includes(key);
      onSelectionChange(
        'disciplines',
        wasAlreadySelected
          ? selected?.disciplines?.filter((s) => s !== key)
          : [...selected?.disciplines, key]
      );
    },
    [onSelectionChange, selected]
  );

  const handleOtherFilterClick = useCallback(
    (label) => {
      onSelectionChange(label, !selected[label]);
    },
    [onSelectionChange, selected]
  );

  return (
    <Splide
      hasTrack={false}
      aria-label='quick search filters'
      className='search-quick-filters'
      options={{
        autoWidth: true,
        perMove: 1,
        waitForTransition: false,
        arrows: true,
        pagination: false,
        gap: '0.5rem',
        wheel: true,
        wheelSleep: 20,
        direction: 'ltr',
        omitEnd: true,
      }}
    >
      <SplideTrack>
        {loadingFilters ? (
          <div className='display-flex-row justify-start gap-1r'>
            {Array.from(Array(8)).map((_, i) => (
              <SkeletonSearchFilterCard key={`search-skeleton-${i}`} />
            ))}
          </div>
        ) : (
          <>
            {FILTERS.map((filter) => (
              <SplideSlide key={`search-filter-${filter.label}`}>
                <SearchFilterCard
                  name={filter.name}
                  imageSrc={filter.icon}
                  onClick={() => handleOtherFilterClick(filter.label)}
                  active={selected[filter.label]}
                />
              </SplideSlide>
            ))}

            {DISCIPLINE_FILTERS.map((filter) => (
              <SplideSlide key={`search-filter-${filter.label}`}>
                <SearchFilterCard
                  name={filter.name}
                  imageSrc={filter.icon}
                  onClick={() => handleDisciplineFilterClick(filter.label)}
                  active={selected?.disciplines?.includes(filter.label)}
                />
              </SplideSlide>
            ))}
            {events?.map((event) => (
              <SplideSlide key={`search-filter-${event.id}`}>
                <SearchFilterCard
                  name={event.name}
                  imageSrc={event.icon_url}
                  active={selected?.event_type_ids?.includes(event.id)}
                  onClick={() => handleFilterClick(event.id)}
                />
              </SplideSlide>
            ))}
          </>
        )}
      </SplideTrack>
      <div className='splide__arrows' />
    </Splide>
  );
}

export default MapQuickFilters;
